import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Shield, User, Eye, EyeOff, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddUserModal = ({ isOpen, onClose, onSave, clients, isRTL, t }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('client');
  const [clientId, setClientId] = useState('');
  const [showPass, setShowPass] = useState(false);

  const handleSave = () => {
    if (!username.trim() || !password.trim()) { toast.error(t('error')); return; }
    onSave({ username, password, role, client_id: role === 'client' ? clientId || null : null });
    setUsername(''); setPassword(''); setRole('client'); setClientId('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
        <div className={`flex items-center gap-2.5 mb-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>
            <User className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">{isRTL ? 'إضافة مستخدم' : 'Add User'}</h2>
        </div>
        <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'}`}>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'اسم المستخدم' : 'Username'} <span className="text-red-500">*</span></label>
            <Input data-testid="add-user-username" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'كلمة المرور' : 'Password'} <span className="text-red-500">*</span></label>
            <div className="relative mt-1.5">
              <Input data-testid="add-user-password" type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" />
              <button type="button" onClick={() => setShowPass(!showPass)} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRTL ? 'left-2' : 'right-2'}`}>
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'الصلاحية' : 'Role'}</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1.5" data-testid="add-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{isRTL ? 'مدير' : 'Admin'}</SelectItem>
                <SelectItem value="client">{isRTL ? 'عميل' : 'Client'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === 'client' && (
            <div>
              <label className="text-sm font-medium text-slate-600">{isRTL ? 'ربط بعميل' : 'Link to Client'}</label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1.5" data-testid="add-user-client"><SelectValue placeholder={isRTL ? 'اختر عميل...' : 'Select client...'} /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={`flex gap-3 pt-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button data-testid="save-user-btn" onClick={handleSave} className="flex-1"
              style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>{t('save')}</Button>
            <Button variant="outline" onClick={onClose} className="flex-1">{t('cancel')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit User Modal - Admin can edit username and password
const EditUserModal = ({ isOpen, onClose, onSave, user, clients, isRTL, t }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('client');
  const [clientId, setClientId] = useState('');
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setPassword('');
      setRole(user.role || 'client');
      setClientId(user.client_id || '');
    }
  }, [user]);

  const handleSave = () => {
    if (!username.trim()) { toast.error(t('error')); return; }
    const updateData = { username };
    if (password.trim()) updateData.password = password;
    updateData.role = role;
    updateData.client_id = role === 'client' ? clientId || null : null;
    onSave(updateData);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
        <div className={`flex items-center gap-2.5 mb-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>
            <Pencil className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">{isRTL ? 'تعديل المستخدم' : 'Edit User'}</h2>
        </div>
        <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'}`}>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'اسم المستخدم' : 'Username'} <span className="text-red-500">*</span></label>
            <Input data-testid="edit-user-username" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'كلمة المرور الجديدة' : 'New Password'}</label>
            <p className="text-xs text-slate-400 mb-1">{isRTL ? 'اتركها فارغة للإبقاء على الحالية' : 'Leave empty to keep current'}</p>
            <div className="relative">
              <Input data-testid="edit-user-password" type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" placeholder={isRTL ? '••••••••' : '••••••••'} />
              <button type="button" onClick={() => setShowPass(!showPass)} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRTL ? 'left-2' : 'right-2'}`}>
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'الصلاحية' : 'Role'}</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1.5" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{isRTL ? 'مدير' : 'Admin'}</SelectItem>
                <SelectItem value="client">{isRTL ? 'عميل' : 'Client'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === 'client' && (
            <div>
              <label className="text-sm font-medium text-slate-600">{isRTL ? 'ربط بعميل' : 'Link to Client'}</label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1.5" data-testid="edit-user-client"><SelectValue placeholder={isRTL ? 'اختر عميل...' : 'Select client...'} /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={`flex gap-3 pt-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button data-testid="save-edit-user-btn" onClick={handleSave} className="flex-1"
              style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>{t('save')}</Button>
            <Button variant="outline" onClick={onClose} className="flex-1">{t('cancel')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Users = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/clients`)
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleAddUser = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      toast.success(t('success'));
      setShowAdd(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm(t('areYouSure'))) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success(t('success'));
        fetchData();
      } catch (error) { toast.error(t('error')); }
    }
  };

  const handleEditUser = async (userData) => {
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, userData);
      toast.success(t('success'));
      setShowEdit(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--theme-accent)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-2xl font-bold text-slate-900">{t('users')}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{isRTL ? 'إدارة مستخدمي النظام' : 'Manage system users'}</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => setShowAdd(true)} className="h-9 px-4 text-sm"
          style={{ backgroundColor: 'var(--theme-primary)', color: 'var(--theme-primary-foreground)' }}>
          <Plus className={`w-4 h-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
          {isRTL ? 'إضافة مستخدم' : 'Add User'}
        </Button>
      </div>

      <Card className="bg-white border-slate-200/80 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {/* Desktop Table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className={`bg-slate-50/60 hover:bg-slate-50/60 ${isRTL ? '[direction:rtl]' : ''}`}>
                  <TableHead className={`font-semibold text-slate-500 text-[11px] uppercase tracking-wider py-2.5 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'المستخدم' : 'User'}</TableHead>
                  <TableHead className={`font-semibold text-slate-500 text-[11px] uppercase tracking-wider py-2.5 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'البريد' : 'Email'}</TableHead>
                  <TableHead className={`font-semibold text-slate-500 text-[11px] uppercase tracking-wider py-2.5 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'الصلاحية' : 'Role'}</TableHead>
                  <TableHead className={`font-semibold text-slate-500 text-[11px] uppercase tracking-wider py-2.5 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'العميل المرتبط' : 'Linked Client'}</TableHead>
                  <TableHead className="w-[60px] py-2.5"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className={`hover:bg-slate-50/60 border-b border-slate-100/80 ${isRTL ? '[direction:rtl]' : ''}`}>
                    <TableCell className={`py-3 ${isRTL ? 'text-right' : ''}`}>
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: u.role === 'admin' ? 'var(--theme-primary)' : 'var(--theme-accent)', color: u.role === 'admin' ? 'var(--theme-primary-foreground)' : 'var(--theme-accent-foreground)' }}>
                          {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        </div>
                        <span className="text-sm font-medium text-slate-800">{u.username}</span>
                      </div>
                    </TableCell>
                    <TableCell className={`py-3 text-sm text-slate-500 ${isRTL ? 'text-right' : ''}`}>{u.email || '-'}</TableCell>
                    <TableCell className={`py-3 ${isRTL ? 'text-right' : ''}`}>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                        {u.role === 'admin' ? (isRTL ? 'مدير' : 'Admin') : (isRTL ? 'عميل' : 'Client')}
                      </span>
                    </TableCell>
                    <TableCell className={`py-3 text-sm text-slate-500 ${isRTL ? 'text-right' : ''}`}>{u.client_id ? getClientName(u.client_id) : '-'}</TableCell>
                    <TableCell className="py-3">
                      <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <Button size="sm" variant="ghost" data-testid={`edit-user-btn-${u.id}`} onClick={() => { setSelectedUser(u); setShowEdit(true); }} className="h-7 w-7 p-0 hover:bg-blue-50">
                          <Pencil className="w-3.5 h-3.5 text-blue-500" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-user-btn-${u.id}`} onClick={() => handleDeleteUser(u.id)} className="h-7 w-7 p-0 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {users.map((u) => (
              <div key={u.id} className={`p-3 ${isRTL ? 'text-right' : ''}`}>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: u.role === 'admin' ? 'var(--theme-primary)' : 'var(--theme-accent)', color: u.role === 'admin' ? 'var(--theme-primary-foreground)' : 'var(--theme-accent-foreground)' }}>
                      {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{u.username}</p>
                      <p className="text-[11px] text-slate-400">{u.email || '-'}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                    {u.role === 'admin' ? (isRTL ? 'مدير' : 'Admin') : (isRTL ? 'عميل' : 'Client')}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {users.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">{t('noData')}</div>}
        </CardContent>
      </Card>

      <AddUserModal isOpen={showAdd} onClose={() => setShowAdd(false)} onSave={handleAddUser} clients={clients} isRTL={isRTL} t={t} />
      <EditUserModal isOpen={showEdit} onClose={() => { setShowEdit(false); setSelectedUser(null); }} onSave={handleEditUser} user={selectedUser} clients={clients} isRTL={isRTL} t={t} />
    </div>
  );
};
